import type { ButtonHTMLAttributes, ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'accent' | 'outline' | 'ghost' | 'subtle' | 'danger' | 'whatsapp' | 'light'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const base = 'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap transition-all duration-200 select-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/40 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]'

const variants: Record<ButtonVariant, string> = {
    primary: 'bg-navy-900 text-white shadow-sm hover:bg-navy-800 hover:shadow-md',
    accent: 'bg-cyan-500 text-navy-950 shadow-sm hover:bg-cyan-400 hover:shadow-md',
    outline: 'border border-navy-200 bg-white text-navy-900 hover:border-navy-300 hover:bg-navy-50',
    ghost: 'text-navy-800 hover:bg-navy-50',
    subtle: 'bg-navy-50 text-navy-900 hover:bg-navy-100',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    whatsapp: 'bg-[#1fa855] text-white shadow-sm hover:bg-[#1a9049] hover:shadow-md',
    light: 'bg-white text-navy-950 shadow-sm hover:bg-white/90'
}

const sizes: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-5 text-[15px]',
    lg: 'h-13 px-7 text-base',
    icon: 'size-11',
    'icon-sm': 'size-9'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string =>
    cn(base, variants[variant], sizes[size], className)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    children?: ReactNode
}

export const Button: FC<ButtonProps> = ({ variant = 'primary', size = 'md', loading = false, className, children, disabled, type = 'button', ...props }) => {
    return (
        <button
            type={type}
            className={buttonClasses(variant, size, className)}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            {...props}
        >
            {loading && <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />}
            {children}
        </button>
    )
}

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({ variant = 'primary', size = 'md', className, ...props }) => {
    return <Link className={buttonClasses(variant, size, className)} {...props} />
}
